import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useLenis } from "@/hooks/useLenis";

// Landing (marketing) sections
import { Nav } from "@/components/site/Nav";
import { Hero } from "@/components/site/Hero";
import { MarqueeBand } from "@/components/site/MarqueeBand";
import { Process } from "@/components/site/Process";
import { Features } from "@/components/site/Features";
import { Portal } from "@/components/site/Portal";
import { UseCases } from "@/components/site/UseCases";
import { Pricing } from "@/components/site/Pricing";
import { EnasusproSwitch } from "@/components/site/EnasusproSwitch";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { EnasusproWidget } from "@/components/site/EnasusproWidget";

// Dashboard app
import { I18nProvider } from "@/dashboard/i18n";
import { AuthProvider, useAuth } from "@/dashboard/auth";
import { Login, Register, ProtectedRoute } from "@/dashboard/AuthPages";
import WelcomeSegment from "@/dashboard/WelcomeSegment";
import Docs from "@/pages/Docs";
import DashboardLayout from "@/dashboard/DashboardLayout";
import Overview from "@/dashboard/pages/Overview";
import Entity from "@/dashboard/pages/Entity";
import Facilities from "@/dashboard/pages/Facilities";
import Clients from "@/dashboard/pages/Clients";
import Trends from "@/dashboard/pages/Trends";
import ScopePage from "@/dashboard/pages/ScopePage";
import EmissionFactors from "@/dashboard/pages/EmissionFactors";
import Glossary from "@/dashboard/pages/Glossary";
import Reports from "@/dashboard/pages/Reports";
import Branding from "@/dashboard/pages/Branding";
import CbamPage from "@/dashboard/pages/CbamPage";
import Targets from "@/dashboard/pages/Targets";
import Intensity from "@/dashboard/pages/Intensity";
import Verification from "@/dashboard/pages/Verification";
import CarbonTax from "@/dashboard/pages/CarbonTax";
import Esrs from "@/dashboard/pages/Esrs";
import Gri from "@/dashboard/pages/Gri";
import IfrsS1 from "@/dashboard/pages/IfrsS1";
import IfrsS2 from "@/dashboard/pages/IfrsS2";
import Profile from "@/dashboard/pages/Profile";
import PricingPage from "@/dashboard/Pricing";
import SupplierPortal from "@/dashboard/pages/SupplierPortal";
import VerifyEmail from "@/dashboard/VerifyEmail";
import { ForgotPassword, ResetPassword } from "@/dashboard/PasswordReset";

const Landing = () => {
  useLenis();
  return (
    <div className="App" data-testid="landing-page">
      <Nav />
      <main>
        <Hero /><MarqueeBand /><Process /><Features /><Portal />
        <UseCases /><Pricing /><EnasusproSwitch /><Contact />
      </main>
      <Footer />
      <EnasusproWidget />
    </div>
  );
};

function RootRoute() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return <div className="grid min-h-screen place-items-center bg-[#F5F6F1] text-sm text-[#6b7268]">…</div>;
  }
  if (user === false) {
    // Unauthenticated: welcome/segment selector at "/", redirect protected paths to /login.
    if (location.pathname === "/" || location.pathname === "") return <WelcomeSegment />;
    return <Navigate to="/login" replace />;
  }
  if (user.role === "supplier") return <SupplierPortal />;
  return <DashboardLayout />;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster theme="light" position="top-center" richColors />
      <I18nProvider>
        <AuthProvider>
          <Routes>
            <Route path="/landing" element={<Landing />} />
            <Route path="/welcome" element={<WelcomeSegment />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<RootRoute />}>
              <Route index element={<Overview />} />
              <Route path="entity" element={<Entity />} />
              <Route path="facilities" element={<Facilities />} />
              <Route path="clients" element={<Clients />} />
              <Route path="trends" element={<Trends />} />
              <Route path="reports" element={<Reports />} />
              <Route path="branding" element={<Branding />} />
              <Route path="scope-1" element={<ScopePage scope={1} />} />
              <Route path="scope-2" element={<ScopePage scope={2} />} />
              <Route path="scope-3" element={<ScopePage scope={3} />} />
              <Route path="emission-factors" element={<EmissionFactors />} />
              <Route path="targets" element={<Targets />} />
              <Route path="intensity" element={<Intensity />} />
              <Route path="verification" element={<Verification />} />
              <Route path="cbam" element={<CbamPage />} />
              <Route path="carbon-tax" element={<CarbonTax />} />
              <Route path="esrs" element={<Esrs />} />
              <Route path="gri" element={<Gri />} />
              <Route path="ifrs-s1" element={<IfrsS1 />} />
              <Route path="ifrs-s2" element={<IfrsS2 />} />
              <Route path="glossary" element={<Glossary />} />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Routes>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
